/**
 * Pairs the browser's GTM conversion events with the backend's server-side conversion, so Google
 * Ads can deduplicate the two (same `transaction_id` on both).
 *
 * The backend returns `X-Conversion-Event-Id: <event>=<uuid>[,<event>=<uuid>...]` on the API
 * response that records conversions (only for website requests with ads consent). One request can
 * record several (an OAuth sign-up records sign_up and login), so each id is keyed by its GA4 event
 * name. Browser fetchers remember them here and `sendGoogleTagEvent` attaches the id matching the
 * event it pushes. Browser-only: module state on the server would leak between requests.
 */
export const CONVERSION_EVENT_ID_HEADER = 'x-conversion-event-id';

// GA4 event names pushed for the conversions the backend also sends server-side.
export const SERVER_CONVERSION_EVENTS = ['sign_up', 'job_apply', 'interview_answer', 'add-job-activated'];

const MAX_AGE_MS = 60_000;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isConversionEventId = (value: string | undefined | null): value is string => !!value && UUID_PATTERN.test(value);

/** Parses the header value into `{ eventName: uuid }`, ignoring anything malformed. */
export const parseConversionEventIds = (headerValue: string | undefined | null): Record<string, string> => {
  const ids: Record<string, string> = {};
  (headerValue ?? '').split(',').forEach((pair) => {
    const [name, id] = pair.trim().split('=');
    if (name && isConversionEventId(id)) {
      ids[name] = id;
    }
  });

  return ids;
};

/** The id for one event from a response's headers, e.g. in server-side auth callbacks. */
export const conversionEventIdFor = (headers: Headers, eventName: string): string =>
  parseConversionEventIds(headers.get(CONVERSION_EVENT_ID_HEADER))[eventName] ?? '';

let pending: { ids: Record<string, string>; at: number } | null = null;

export const rememberConversionEventId = (headers: Headers | undefined | null): void => {
  if (typeof window === 'undefined' || !headers) return;
  const ids = parseConversionEventIds(headers.get(CONVERSION_EVENT_ID_HEADER));
  if (Object.keys(ids).length > 0) {
    pending = { ids, at: Date.now() };
  }
};

/** Returns the remembered id for this event once (then forgets it), or undefined if none or too old. */
export const takeConversionEventId = (eventName: string): string | undefined => {
  if (!pending || Date.now() - pending.at > MAX_AGE_MS) {
    pending = null;

    return undefined;
  }
  const id = pending.ids[eventName];
  delete pending.ids[eventName];

  return id;
};
